import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Send,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { FadeIn } from '../../animations';

/**
 * Footer V2 - Enhanced with animations
 * Features:
 * - Multi-column layout
 * - Newsletter subscription
 * - Social media links
 * - Sitemap
 */

const FooterContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(180deg, #0E1C2A 0%, #071018 100%)',
  borderTop: 'none',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(4),
  marginTop: 0,
  color: '#F7F8FA',
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: '#F7F8FA',
  fontFamily: "'Fraunces', Georgia, serif",
}));

const FooterLink = styled(RouterLink)(({ theme }) => ({
  color: 'rgba(247,248,250,0.72)',
  textDecoration: 'none',
  fontSize: '0.95rem',
  display: 'block',
  marginBottom: theme.spacing(1),
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#fff',
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  color: '#F7F8FA',
  border: '1px solid rgba(247,248,250,0.25)',
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.08)',
    color: 'white',
  },
}));

const FooterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const NewsletterInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: 'white',
  },
  '& .MuiInputBase-input': {
    color: '#0E1C2A',
  },
}));

const CopyrightBox = styled(Box)(({ theme }) => ({
  borderTop: '1px solid rgba(247,248,250,0.12)',
  paddingTop: theme.spacing(3),
  marginTop: theme.spacing(4),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    textAlign: 'center',
  },
}));

const FooterV2 = () => {
  const [email, setEmail] = React.useState('');

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
    setEmail('');
  };

  return (
    <FooterContainer component={motion.div}>
      <Container maxWidth="xl">
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Column 1: About */}
          <Grid item xs={12} sm={6} md={3}>
            <FadeIn delay={0.1}>
              <FooterSection>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#F7F8FA',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontFamily: "'Fraunces', Georgia, serif",
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #1F6F78 0%, #2A8F9A 100%)',
                    }}
                  />
                  Rehman Travel
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'rgba(247,248,250,0.72)' }}>
                  Travel agency for Umrah, ziyarat, international holidays, and
                  Pakistan trips — hotels, transport, and meals in one booking.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <SocialIconButton size="small">
                    <Facebook fontSize="small" />
                  </SocialIconButton>
                  <SocialIconButton size="small">
                    <Twitter fontSize="small" />
                  </SocialIconButton>
                  <SocialIconButton size="small">
                    <Instagram fontSize="small" />
                  </SocialIconButton>
                  <SocialIconButton size="small">
                    <LinkedIn fontSize="small" />
                  </SocialIconButton>
                </Box>
              </FooterSection>
            </FadeIn>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <FadeIn delay={0.2}>
              <FooterSection>
                <FooterTitle variant="subtitle1">Quick Links</FooterTitle>
                <FooterLink to="/v2/packages?category=International">Trips</FooterLink>
                <FooterLink to="/v2/packages?category=Umrah">Umrah</FooterLink>
                <FooterLink to="/v2/packages?category=Ziyarat">Ziyarat</FooterLink>
                <FooterLink to="/v2/customized-booking">Customize</FooterLink>
                <FooterLink to="/v2/contact">Become a partner</FooterLink>
              </FooterSection>
            </FadeIn>
          </Grid>

          {/* Column 3: Support */}
          <Grid item xs={12} sm={6} md={2}>
            <FadeIn delay={0.3}>
              <FooterSection>
                <FooterTitle variant="subtitle1">Support</FooterTitle>
                <FooterLink to="/v2/help">Help Center & Services</FooterLink>
                <FooterLink to="/v2/contact">Contact Us</FooterLink>
                <FooterLink to="/v2/flights/track">Track flight request</FooterLink>
                <FooterLink to="/v2/dashboard">Track Booking</FooterLink>
                <FooterLink to="/v2/help">Cancellation Policy</FooterLink>
                <FooterLink to="/v2/help">Terms & Conditions</FooterLink>
              </FooterSection>
            </FadeIn>
          </Grid>

          {/* Column 4: Legal */}
          <Grid item xs={12} sm={6} md={2}>
            <FadeIn delay={0.4}>
              <FooterSection>
                <FooterTitle variant="subtitle1">Legal</FooterTitle>
                <FooterLink to="/privacy">Privacy Policy</FooterLink>
                <FooterLink to="/terms">Terms of Service</FooterLink>
                <FooterLink to="/refund-policy">Refund Policy</FooterLink>
                <FooterLink to="/cookie-policy">Cookie Policy</FooterLink>
                <FooterLink to="/accessibility">Accessibility</FooterLink>
              </FooterSection>
            </FadeIn>
          </Grid>

          {/* Column 5: Newsletter */}
          <Grid item xs={12} md={3}>
            <FadeIn delay={0.5}>
              <FooterSection>
                <FooterTitle variant="subtitle1">Newsletter</FooterTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Subscribe to get special offers and updates on new packages.
                </Typography>
                <Box component="form" onSubmit={handleNewsletterSubmit}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <NewsletterInput
                      size="small"
                      placeholder="Your email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        borderRadius: '50%',
                        minWidth: 44,
                        height: 44,
                        padding: 0,
                      }}
                      type="submit"
                    >
                      <Send fontSize="small" />
                    </Button>
                  </Box>
                </Box>
              </FooterSection>
            </FadeIn>
          </Grid>
        </Grid>

        {/* Copyright Section */}
        <CopyrightBox>
          <Typography variant="body2" sx={{ color: 'rgba(247,248,250,0.55)' }}>
            © {new Date().getFullYear()} Rehman Travel. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link href="/privacy" underline="hover" variant="body2" sx={{ color: 'rgba(247,248,250,0.7)' }}>
              Privacy Policy
            </Link>
            <Link href="/terms" underline="hover" variant="body2" sx={{ color: 'rgba(247,248,250,0.7)' }}>
              Terms of Service
            </Link>
            <Link href="/contact" underline="hover" variant="body2" sx={{ color: 'rgba(247,248,250,0.7)' }}>
              Contact Us
            </Link>
          </Box>
        </CopyrightBox>
      </Container>
    </FooterContainer>
  );
};

export default FooterV2;
